import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { isTransactionsTransitionAlreadyReviewed } from '../../util/errors';
import { Form, Button, FieldRadioButton, FieldTextInput, SecondaryButton } from '../../components';

import css from './ReviewForm.css';
import { trimDisplayNameLastWord } from '../../util/text';
import config from '../../config';
import { anonymizeOtherParty } from '../../util/anonym';

const { userTypeRider } = config;

const ownerOptions = ['distance', 'availability', 'experience', 'horse-found', 'other'];
const riderOptions = ['distance', 'age', 'availability', 'experience', 'rider-found', 'other'];

const CHOOSE_REASON = 'choose-reason';
const LEAVE_FEEDBACK = 'leave-feedback';

const ReviewForm = ({
    otherPartyUserType,
    otherParty,
    currentUser,
    currentListing,
    intl,
    ...restProps
}) => {
    const transactionRequestInProgress = useSelector(
        s => s.TransactionPage.transactionRequestInProgress
    );
    const [view, setView] = useState(CHOOSE_REASON);
    const [finalForm, setFinalForm] = useState(null);
    const [reasonValue, setReasonValue] = useState(null);

    const isChooseReasonForm = view === CHOOSE_REASON;

    const { displayName } = otherParty.attributes.profile;
    /**
     * Swap user & other party to determine current user anonym/deanon name
     */
    const currentUserAnonymized = anonymizeOtherParty(currentUser, otherParty, currentListing);

    const {
        firstName,
        publicData: { userType: currentUserType },
    } = currentUserAnonymized.attributes.profile;

    useEffect(() => {
        if (!finalForm) return;
        const { formatMessage } = intl;

        const id = `ReviewForm.option-${reasonValue || 'other'}-description-for-${currentUserType}`;

        finalForm.change(
            'feedback',
            `Hallo ${trimDisplayNameLastWord(displayName)}

${formatMessage({ id })}

Beste Grüsse,
${firstName}`
        );
    }, [view]);

    return (
        <FinalForm
            {...restProps}
            render={fieldRenderProps => {
                const {
                    setRefreshModalScrollPosKey,
                    className,
                    rootClassName,
                    handleSubmit,
                    invalid,
                    sendReviewError,
                    values,
                    form,
                } = fieldRenderProps;

                const { reason } = values;

                if (form && !finalForm) {
                    setFinalForm(form);
                }

                const classes = classNames(rootClassName || css.root, className);
                const newStepDisabled = invalid || !reason || transactionRequestInProgress;
                const options = otherPartyUserType === userTypeRider ? riderOptions : ownerOptions;

                return (
                    <Form className={classes} onSubmit={handleSubmit}>
                        {isChooseReasonForm && (
                            <>
                                <p className={css.reason}>
                                    <FormattedMessage id={`ReviewForm.reason-${CHOOSE_REASON}`} />
                                </p>
                                <div>
                                    {options.map(option => (
                                        <FieldRadioButton
                                            key={option}
                                            form={form}
                                            notifyOnChange={value => setReasonValue(value)}
                                            id={`${option}-option`}
                                            name="reason"
                                            label={
                                                <p className={css.option}>
                                                    <FormattedMessage
                                                        id={`ReviewForm.option-${option}`}
                                                    />
                                                </p>
                                            }
                                            value={option}
                                        />
                                    ))}
                                </div>
                            </>
                        )}

                        {!isChooseReasonForm && (
                            <FieldTextInput
                                id="feedback"
                                name="feedback"
                                type="textarea"
                                className={css.commentField}
                                maxLength={1000}
                                placeholder={intl.formatMessage({
                                    id: 'ReviewForm.feedback',
                                })}
                            />
                        )}

                        {sendReviewError ? (
                            <p className={css.error}>
                                <FormattedMessage
                                    id={`ReviewForm.${
                                        isTransactionsTransitionAlreadyReviewed(sendReviewError)
                                            ? 'reviewSubmitAlreadySent'
                                            : 'reviewSubmitFailed'
                                    }`}
                                />
                            </p>
                        ) : null}

                        <footer>
                            {isChooseReasonForm ? (
                                <Button
                                    type="button"
                                    inProgress={transactionRequestInProgress}
                                    disabled={newStepDisabled}
                                    onClick={() => {
                                        setView(LEAVE_FEEDBACK);
                                        setRefreshModalScrollPosKey(LEAVE_FEEDBACK);
                                    }}
                                >
                                    <FormattedMessage id="ReviewForm.newStep" />
                                </Button>
                            ) : (
                                <SecondaryButton
                                    type="submit"
                                    className={css.submitButton}
                                    inProgress={transactionRequestInProgress}
                                    disabled={transactionRequestInProgress}
                                >
                                    <FormattedMessage id="ReviewForm.submitAction" />
                                </SecondaryButton>
                            )}
                        </footer>
                    </Form>
                );
            }}
        />
    );
};

export default compose(injectIntl)(ReviewForm);
